import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Card, Select, Button, Space, Typography,
    message, Dropdown, Modal, Tag
} from 'antd';
import {
    PlusOutlined, EditOutlined,
    DeleteOutlined, MoreOutlined, SafetyCertificateOutlined,
    GlobalOutlined, TeamOutlined
} from '@ant-design/icons';
import { roleService } from '../services/api';
import RoleDrawer from '../components/RoleDrawer';
import RolePermissionsDrawer from '../components/RolePermissionsDrawer';
import PortalChangeModal from '../components/PortalChangeModal';

const { Title, Text } = Typography;
const { Option } = Select;

const PRIMARY_RED = '#DA291C';

const RolesPage = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [portal, setPortal] = useState(null); // null = All, 'admin', 'agent'

    const [drawer, setDrawer] = useState({
        visible: false,
        mode: 'create',
        roleData: null
    });

    const [permissionsDrawer, setPermissionsDrawer] = useState({
        visible: false,
        roleUuid: null,
        roleName: ''
    });

    const [portalModal, setPortalModal] = useState({
        visible: false,
        roleUuid: null,
        currentPortal: ''
    });

    const fetchRoles = useCallback(async () => {
        setLoading(true);
        try {
            const response = await roleService.list(portal);
            if (response.status === 'success') {
                setData(response.data);
            }
        } catch (error) {
            message.error('Error al cargar roles');
        } finally {
            setLoading(false);
        }
    }, [portal]);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    const openCreateDrawer = () => {
        setDrawer({ visible: true, mode: 'create', roleData: null });
    };

    const openEditDrawer = (record) => {
        setDrawer({ visible: true, mode: 'edit', roleData: record });
    };

    const openPermissionsDrawer = (record) => {
        setPermissionsDrawer({
            visible: true,
            roleUuid: record.uuid,
            roleName: record.name
        });
    };

    const openPortalModal = (record) => {
        setPortalModal({
            visible: true,
            roleUuid: record.uuid,
            currentPortal: record.portal
        });
    };

    const handleDuplicate = async (record) => {
        Modal.confirm({
            title: 'Duplicar Rol',
            content: `¿Desea crear una copia del rol "${record.name}"?`,
            okText: 'Duplicar',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    const res = await roleService.duplicate(record.uuid);
                    if (res.status === 'success') {
                        message.success('Rol duplicado exitosamente');
                        fetchRoles();
                    }
                } catch (error) {
                    message.error(error.response?.data?.message || 'Error al duplicar rol');
                }
            }
        });
    };

    const handleDelete = (record) => {
        // According to workflow, if it has permissions it needs a target_role_uuid
        // For now, let's show basic delete and handle logic inside a specialized modal if needed
        // Or simple confirmation if no critical constraints are met.
        // The workflow says: "If the role to delete has direct permissions... the system MUST exigir a target_role_uuid"

        Modal.confirm({
            title: '¿Eliminar rol?',
            content: `¿Estás seguro que deseas eliminar el rol "${record.name}"?`,
            okText: 'Sí, Eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    const res = await roleService.delete(record.uuid);
                    if (res.status === 'success') {
                        message.success('Rol eliminado');
                        fetchRoles();
                    }
                } catch (error) {
                    message.error(error.response?.data?.message || 'Error al eliminar rol');
                }
            }
        });
    };

    const columns = [
        {
            title: 'Nombre',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Portal',
            dataIndex: 'portal',
            key: 'portal',
            render: (val) => (
                <Tag color={val === 'admin' ? 'volcano' : 'blue'}>
                    {(val || '').toUpperCase()}
                </Tag>
            )
        },

        {
            title: 'Usuarios',
            dataIndex: 'user_count',
            key: 'user_count',
            render: (count) => (
                <Space>
                    <TeamOutlined style={{ color: '#8c8c8c' }} />
                    <Text>{count || 0}</Text>
                </Space>
            )
        },
        {
            title: 'Permisos',
            dataIndex: 'permission_count',
            key: 'permission_count',
            render: (count) => (
                <Tag color="purple">{count || 0} directos</Tag>
            )
        },
        {
            title: '',
            key: 'actions',
            width: 80,
            render: (_, record) => {
                const items = [
                    {
                        key: 'edit',
                        label: 'Editar Datos',
                        icon: <EditOutlined />,
                        onClick: () => openEditDrawer(record)
                    },
                    {
                        key: 'permissions',
                        label: 'Gestionar Permisos',
                        icon: <SafetyCertificateOutlined />,
                        onClick: () => openPermissionsDrawer(record)
                    },
                    {
                        key: 'portal',
                        label: 'Cambiar Portal',
                        icon: <GlobalOutlined />,
                        onClick: () => openPortalModal(record)
                    },
                    {
                        key: 'duplicate',
                        label: 'Duplicar Rol',
                        icon: <PlusOutlined />,
                        onClick: () => handleDuplicate(record)
                    },
                    {
                        type: 'divider'
                    },
                    {
                        key: 'delete',
                        label: 'Eliminar Rol',
                        icon: <DeleteOutlined />,
                        danger: true,
                        onClick: () => handleDelete(record)
                    }
                ];

                return (
                    <Dropdown menu={{ items }} trigger={['click']}>
                        <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                );
            }
        }
    ];

    return (
        <Space direction="vertical" size="large" style={{ display: 'flex', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2} style={{ margin: 0 }}>Gestión de Roles</Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    style={{ background: PRIMARY_RED, borderColor: PRIMARY_RED }}
                    onClick={openCreateDrawer}
                >
                    Nuevo Rol
                </Button>
            </div>

            <Card bodyStyle={{ padding: '16px' }}>
                <Space wrap size="middle">
                    <Select
                        placeholder="Filtrar por Portal"
                        style={{ width: 220 }}
                        onChange={(val) => setPortal(val || null)}
                        allowClear
                    >
                        <Option value="admin">Portal Administrador</Option>
                        <Option value="agent">Portal Agente</Option>
                    </Select>
                    <Text type="secondary">
                        <GlobalOutlined /> Gestión de Roles del Sistema
                    </Text>
                </Space>
            </Card>

            <Table
                columns={columns}
                dataSource={data}
                rowKey="uuid"
                loading={loading}
                pagination={{
                    showTotal: (total) => `${total} roles configurados`,
                    pageSize: 10
                }}
            />

            <RoleDrawer
                visible={drawer.visible}
                mode={drawer.mode}
                roleData={drawer.roleData}
                onClose={() => setDrawer({ ...drawer, visible: false })}
                onSuccess={fetchRoles}
            />

            <RolePermissionsDrawer
                visible={permissionsDrawer.visible}
                roleUuid={permissionsDrawer.roleUuid}
                roleName={permissionsDrawer.roleName}
                onClose={() => setPermissionsDrawer({ ...permissionsDrawer, visible: false })}
                onSuccess={fetchRoles}
            />

            <PortalChangeModal
                visible={portalModal.visible}
                roleUuid={portalModal.roleUuid}
                currentPortal={portalModal.currentPortal}
                onClose={() => setPortalModal({ ...portalModal, visible: false })}
                onSuccess={fetchRoles}
            />
        </Space>
    );
};

export default RolesPage;
